import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchArchiveJobs, refreshArchiveJobs, deleteJob, setCurrentJob, updateJobInArray } from '../../redux/features/jobSlice';
import { getWorkers } from '../../redux/features/workerSlice';
import { getPartners } from '../../redux/features/partnerSlice';
import { Calendar, Archive, ChevronLeft, ChevronRight, Edit, Trash2, X, DollarSign, Printer, Search, Filter, XCircle, PlusCircle } from 'lucide-react';
import { getWorkerName, getWorkerInitials, getAssigneeColor } from '../../utils/workerUtils';
import { applyAssigneeFilters, EMPTY_ARCHIVE_EXTRA_FILTERS, hasActiveArchiveExtraFilters, jobMatchesArchiveExtraFilters, jobMatchesArchiveSearch } from '../../utils/archiveFilterUtils';
import { resolveOutgoingHandoffPartnerAssignee } from '../../utils/handoffSettlementUtils';
import {
  formatDisplayValue,
  parseMoneyAmountSR,
  getArchivedJobTimelineDate,
  getArchivedJobLocalDayKey,
  formatLocalDateKey
} from '../../utils/displayUtils';
import { computeCalendarRange } from '../../utils/calendarRangeUtils';
import { getBusinessType } from '../../utils/businessTypeUtils';
import { fetchDeviceCategories } from '../../redux/api';
import { logPeriodJobsTable } from '../../utils/periodDebugLog';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { toast } from 'react-toastify';
import JobForm from '../forms/JobForm';
import ReportEditForm from '../forms/ReportEditForm';
import DatePickerField from '../forms/job/DatePickerField';
import WorkOrderPrintModal from '../jobs/WorkOrderPrintModal';

const ArchiveView = () => {
  const dispatch = useDispatch();
  const { archiveJobs, archiveLoading } = useSelector((state) => state.job);
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { partners } = useSelector((state) => state.partner || { partners: [] });
  const { user } = useSelector((state) => state.auth);
  const { socket, isConnected } = useWebSocket();
  
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedJob, setSelectedJob] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReportEditModalOpen, setIsReportEditModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [createJobPrefill, setCreateJobPrefill] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchAllArchive, setSearchAllArchive] = useState(false);
  const [showExtraFilters, setShowExtraFilters] = useState(false);
  const [extraFilters, setExtraFilters] = useState({ ...EMPTY_ARCHIVE_EXTRA_FILTERS });
  const [deviceCategories, setDeviceCategories] = useState([]);
  
  // Handle edit job
  const handleEditJob = () => {
    if (selectedJob) {
      setIsPrintModalOpen(false);
      dispatch(setCurrentJob(selectedJob._id));
      setIsEditModalOpen(true);
    }
  };
  
  // Handle edit report
  const handleEditReport = () => {
    if (selectedJob) {
      setIsPrintModalOpen(false);
      setIsReportEditModalOpen(true);
    }
  };
  
  // Handle delete job
  const handleDeleteJob = async () => {
    if (!selectedJob) return;
    
    if (window.confirm(`Da li ste sigurni da želite da obrišete ovaj arhivirani posao?\n\nKlijent: ${selectedJob.clientName || '-'}\nAdresa: ${selectedJob.clientAddress || '-'}\n\nOva akcija je nepovratna!`)) {
      try {
        await dispatch(deleteJob(selectedJob._id)).unwrap();
        toast.success('Posao uspešno obrisan');
        setSelectedJob(null);
        // Refresh jobs
        dispatch(refreshArchiveJobs());
      } catch (error) {
        toast.error(error.message || 'Greška pri brisanju posla');
      }
    }
  };
  
  // Handle close edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedJob(null);
    dispatch(refreshArchiveJobs());
  };
  
  // Handle close report edit modal
  const handleCloseReportEditModal = () => {
    setIsReportEditModalOpen(false);
    dispatch(refreshArchiveJobs());
  };

  const buildPrefillFromArchivedJob = (job) => {
    if (!job) return null;
    return {
      clientName: job.clientName || '',
      clientPhone: job.clientPhone || '',
      clientEmail: job.clientEmail || '',
      clientAddress: job.clientAddress,
      clientId: job.clientId || undefined,
      isBusinessClient: !!job.isBusinessClient,
      businessClient: job.businessClient || undefined,
      deviceType: job.deviceType || '',
      deviceBrand: job.deviceBrand || '',
      deviceModel: job.deviceModel || '',
      deviceSerialNumber: job.deviceSerialNumber || '',
      deviceCategoryId: job.deviceCategoryId || '',
      deviceCategoryName: job.deviceCategoryName || '',
      serviceLocation: job.serviceLocation || 'OnSite',
      hasWarranty: job.hasWarranty,
      warranty: job.hasWarranty === true || job.hasWarranty === 'yes',
      issueDescription: ''
    };
  };

  const handleCreateJobForSameClient = () => {
    if (!selectedJob) return;
    setCreateJobPrefill(buildPrefillFromArchivedJob(selectedJob));
    setIsPrintModalOpen(false);
  };
  
  // Handle report edit success
  const handleReportEditSuccess = () => {
    setIsReportEditModalOpen(false);
    setSelectedJob(null);
    dispatch(refreshArchiveJobs());
  };

  const toggleExtraId = (key, id, checked) => {
    setExtraFilters((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      return {
        ...prev,
        [key]: checked ? [...current, id] : current.filter((item) => item !== id)
      };
    });
  };

  const handleSearchAllArchive = () => {
    setSearchAllArchive(true);
  };

  const handleClearArchiveFilters = () => {
    setSearchTerm('');
    setSearchAllArchive(false);
    setExtraFilters({ ...EMPTY_ARCHIVE_EXTRA_FILTERS });
    setSelectedWorkers([]);
    setSelectedPartners([]);
  };

  const extraFiltersActive = hasActiveArchiveExtraFilters(extraFilters);
  const searchActive = Boolean(String(searchTerm || '').trim()) || extraFiltersActive || searchAllArchive;

  /** Opseg za API: cela arhiva pri pretrazi svih, ručni „Od–Do“ ako su oba popunjena, inače vidljiva nedelja ili mesec. */
  const effectiveApiRange = useMemo(() => {
    if (searchAllArchive) {
      return { fetchAll: true };
    }
    if (startDate && endDate) {
      const s = new Date(startDate + 'T00:00:00');
      const e = new Date(endDate + 'T23:59:59.999');
      return { startDate: s.toISOString(), endDate: e.toISOString() };
    }
    return computeCalendarRange(currentDate, viewMode === 'week' ? 'week' : 'month');
  }, [searchAllArchive, startDate, endDate, currentDate, viewMode]);

  useEffect(() => {
    dispatch(getWorkers());
    dispatch(getPartners());
  }, [dispatch]);

  useEffect(() => {
    const loadDeviceCategories = async () => {
      try {
        const response = await fetchDeviceCategories();
        if (response?.data) {
          setDeviceCategories(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        setDeviceCategories([]);
      }
    };
    loadDeviceCategories();
  }, []);

  const currentCompanyId = user?.result?._id || user?.result?.companyId || null;

  useEffect(() => {
    const businessType = user?.result?.businessType || getBusinessType() || 'Home Appliance Technician';
    if (!businessType) return;
    dispatch(
      fetchArchiveJobs({
        businessType,
        ...(effectiveApiRange.fetchAll
          ? { fetchAll: true }
          : { startDate: effectiveApiRange.startDate, endDate: effectiveApiRange.endDate })
      })
    );
  }, [dispatch, user, effectiveApiRange]);

  // WebSocket listener for real-time job updates (especially when jobs are archived)
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    const handleJobUpdate = (data) => {
      if (data.status === 'Archived') {
        dispatch(refreshArchiveJobs());
      } else if (data.status === 'Finished' || data.status === 'Completed') {
        if (data.jobId) {
          dispatch(updateJobInArray({ jobId: data.jobId, updates: { status: data.status } }));
        }
      }
    };

    socket.on('job_updated', handleJobUpdate);

    return () => {
      socket.off('job_updated', handleJobUpdate);
    };
  }, [socket, isConnected, dispatch, user]);

  useEffect(() => {
    const list = Array.isArray(archiveJobs) ? archiveJobs : [];
    let filtered = Array.from(new Map(list.map((job) => [job._id, job])).values());
    filtered = applyAssigneeFilters(
      filtered,
      { workerIds: selectedWorkers, partnerIds: selectedPartners },
      { workers, partners, sourceCompanyId: currentCompanyId }
    );
    filtered = filtered.filter((job) => jobMatchesArchiveSearch(job, searchTerm));
    filtered = filtered.filter((job) =>
      jobMatchesArchiveExtraFilters(job, extraFilters, { deviceCategories })
    );
    filtered.sort((a, b) => {
      const dateA = getArchivedJobTimelineDate(a).getTime();
      const dateB = getArchivedJobTimelineDate(b).getTime();
      return dateB - dateA;
    });
    setFilteredJobs(filtered);

    logPeriodJobsTable('[SpinTasker · Arhiva]', filtered, {
      startDate,
      endDate,
      selectedWorkers,
      selectedPartners,
      workers,
      partners,
      extra: {
        napomena: searchAllArchive
          ? 'Pretraga cele arhive; filteri na klijentu'
          : 'Podaci sa servera po periodu; filter radnika/partnera na klijentu',
        effectiveRange: effectiveApiRange,
      },
    });
  }, [
    archiveJobs,
    startDate,
    endDate,
    selectedWorkers,
    selectedPartners,
    searchTerm,
    extraFilters,
    deviceCategories,
    searchAllArchive,
    workers,
    partners,
    effectiveApiRange,
    currentCompanyId
  ]);

  // Helper: get completed date for a job
  const getCompletedDate = (job) => {
    const d = getArchivedJobTimelineDate(job);
    if (Number.isNaN(d.getTime())) return new Date(NaN);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Map jobs by YYYY-MM-DD key
  const jobsByDate = useMemo(() => {
    const map = {};
    filteredJobs.forEach((job) => {
      const key = getArchivedJobLocalDayKey(job);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(job);
    });
    return map;
  }, [filteredJobs]);

  const startOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday as first day
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const startOfMonthGrid = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const day = firstDay.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    firstDay.setDate(firstDay.getDate() + diff);
    firstDay.setHours(0, 0, 0, 0);
    return firstDay;
  };

  const isSameDay = (a, b) => {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  const dayNames = ['Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota', 'Nedelja'];

  const isInitialArchiveLoad = archiveLoading && (!Array.isArray(archiveJobs) || archiveJobs.length === 0) && !searchAllArchive;

  if (isInitialArchiveLoad) {
    return (
      <div className="h-full bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-900 p-2 sm:p-4">
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-white">Arhiva</h1>
            <p className="text-xs sm:text-sm text-gray-400">Kalendar završenih poslova</p>
          </div>
        </div>

        {/* Top filters: datum + radnik + view mode */}
        <div className="bg-gray-800 rounded-lg p-3 sm:p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <h3 className="text-base sm:text-lg font-semibold text-white">
                Filteri
              </h3>
              {archiveLoading && (
                <span className="text-[10px] sm:text-xs text-gray-400">Učitavanje...</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowExtraFilters((prev) => !prev)}
                className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs sm:text-sm ${
                  showExtraFilters || extraFiltersActive
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                title="Dodatni filteri"
              >
                <Filter className="h-3.5 w-3.5" />
                Više filtera
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1 rounded-md text-xs sm:text-sm ${
                  viewMode === 'week'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Nedelja
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1 rounded-md text-xs sm:text-sm ${
                  viewMode === 'month'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Mesec
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearchAllArchive();
                }}
                placeholder="Pretraži klijenta, telefon, adresu, uređaj, nalog..."
                className="w-full pl-8 pr-3 py-2 text-xs sm:text-sm bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoCapitalize="sentences"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <button
              type="button"
              onClick={handleSearchAllArchive}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm ${
                searchAllArchive
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              }`}
              title="Pretraži sve arhivirane poslove, ne samo trenutni period"
            >
              <Search className="h-4 w-4" />
              Pretraži sve
            </button>
            {(searchAllArchive || extraFiltersActive || searchTerm || selectedWorkers.length > 0 || selectedPartners.length > 0) && (
              <button
                type="button"
                onClick={handleClearArchiveFilters}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs sm:text-sm bg-gray-700 text-gray-300 hover:bg-gray-600"
                title="Obriši filtere"
              >
                <XCircle className="h-4 w-4" />
                Obriši
              </button>
            )}
          </div>

          {searchAllArchive && (
            <p className="text-[11px] sm:text-xs text-blue-300 mb-3">
              Pretraga je po celoj arhivi, ne samo po trenutnoj nedelji ili mesecu.
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Od datuma
              </label>
              <DatePickerField
                value={startDate}
                onChange={setStartDate}
                placeholder="dd.mm.yyyy"
                inputClass="w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Do datuma
              </label>
              <DatePickerField
                value={endDate}
                onChange={setEndDate}
                placeholder="dd.mm.yyyy"
                inputClass="w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Radnici
              </label>
              <div className="max-h-36 overflow-y-auto bg-gray-700 rounded-lg border border-gray-600 p-2 space-y-1">
                {workers && workers.length > 0 ? (
                  workers.map((worker) => {
                    const workerId = String(worker._id);
                    const workerName = `${worker.firstName || ''} ${worker.lastName || ''}`.trim();
                    const isSelected = selectedWorkers.includes(workerId);
                    return (
                      <label
                        key={workerId}
                        className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-600 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            setSelectedWorkers((prev) =>
                              e.target.checked
                                ? [...prev, workerId]
                                : prev.filter((id) => id !== workerId)
                            );
                          }}
                          className="w-3 h-3"
                        />
                        <span>{workerName || worker.email || 'Nepoznat radnik'}</span>
                      </label>
                    );
                  })
                ) : (
                  <p className="text-xs text-gray-400">Nema radnika</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Servisni partneri
              </label>
              <div className="max-h-36 overflow-y-auto bg-gray-700 rounded-lg border border-gray-600 p-2 space-y-1">
                {partners && partners.length > 0 ? (
                  partners.map((partner) => {
                    const partnerId = String(partner._id || partner.id);
                    const isSelected = selectedPartners.includes(partnerId);
                    return (
                      <label
                        key={partnerId}
                        className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-600 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            setSelectedPartners((prev) =>
                              e.target.checked
                                ? [...prev, partnerId]
                                : prev.filter((id) => id !== partnerId)
                            );
                          }}
                          className="w-3 h-3"
                        />
                        <span>{partner.companyName || partner.name || 'Partner'}</span>
                      </label>
                    );
                  })
                ) : (
                  <p className="text-xs text-gray-400">Nema partnera</p>
                )}
              </div>
            </div>
          </div>

          {showExtraFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-3 pt-3 border-t border-gray-700">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                  Garancija
                </label>
                <div className="bg-gray-700 rounded-lg border border-gray-600 p-2 space-y-1">
                  {[
                    { value: 'all', label: 'Svi poslovi' },
                    { value: 'yes', label: 'Pod garancijom' },
                    { value: 'no', label: 'Bez garancije' }
                  ].map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-600 p-1 rounded"
                    >
                      <input
                        type="radio"
                        name="archive-warranty"
                        checked={extraFilters.warranty === option.value}
                        onChange={() => setExtraFilters((prev) => ({ ...prev, warranty: option.value }))}
                        className="w-3 h-3"
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                  Lokacija
                </label>
                <div className="bg-gray-700 rounded-lg border border-gray-600 p-2 space-y-1">
                  {[
                    { value: 'OnSite', label: 'Na adresi' },
                    { value: 'InWorkshop', label: 'U servisu' }
                  ].map((option) => {
                    const isSelected = extraFilters.serviceLocation.includes(option.value);
                    return (
                      <label
                        key={option.value}
                        className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-600 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => toggleExtraId('serviceLocation', option.value, e.target.checked)}
                          className="w-3 h-3"
                        />
                        <span>{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                  Kategorija uređaja
                </label>
                <div className="max-h-36 overflow-y-auto bg-gray-700 rounded-lg border border-gray-600 p-2 space-y-1">
                  {deviceCategories.length > 0 ? (
                    deviceCategories.map((category) => {
                      const categoryId = String(category.id || category._id);
                      const isSelected = extraFilters.deviceCategory.includes(categoryId);
                      return (
                        <label
                          key={categoryId}
                          className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-600 p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => toggleExtraId('deviceCategory', categoryId, e.target.checked)}
                            className="w-3 h-3"
                          />
                          <span>{category.name}</span>
                        </label>
                      );
                    })
                  ) : (
                    <p className="text-xs text-gray-400">Nema kategorija</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                  Dodatno
                </label>
                <div className="bg-gray-700 rounded-lg border border-gray-600 p-2 space-y-1">
                  <label className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={!!extraFilters.returnVisitOnly}
                      onChange={(e) =>
                        setExtraFilters((prev) => ({ ...prev, returnVisitOnly: e.target.checked }))
                      }
                      className="w-3 h-3"
                    />
                    <span>Samo povratne posete</span>
                  </label>
                  {[
                    { value: 'all', label: 'Fiskalni: svi' },
                    { value: 'yes', label: 'Ima fiskalni račun' },
                    { value: 'no', label: 'Bez fiskalnog računa' }
                  ].map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-600 p-1 rounded"
                    >
                      <input
                        type="radio"
                        name="archive-fiscal"
                        checked={extraFilters.fiscal === option.value}
                        onChange={() => setExtraFilters((prev) => ({ ...prev, fiscal: option.value }))}
                        className="w-3 h-3"
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {searchActive && (
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-white">
                Rezultati {searchAllArchive ? 'cele arhive' : 'perioda'}
              </h3>
              <span className="text-xs text-gray-400">
                {filteredJobs.length} {filteredJobs.length === 1 ? 'posao' : 'poslova'}
              </span>
            </div>
            {filteredJobs.length === 0 ? (
              <p className="text-xs text-gray-400">Nema poslova za izabrane filtere.</p>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-1.5">
                {filteredJobs.slice(0, 80).map((job) => {
                  const when = getArchivedJobTimelineDate(job);
                  const dateLabel = Number.isNaN(when.getTime())
                    ? '-'
                    : when.toLocaleDateString('sr-RS');
                  const device = [job.deviceType, job.deviceBrand, job.deviceModel]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <button
                      key={job._id}
                      type="button"
                      onClick={() => {
                        setIsPrintModalOpen(false);
                        setSelectedJob(job);
                      }}
                      className="w-full text-left bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-md px-2.5 py-1.5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs sm:text-sm text-white truncate">
                            {formatDisplayValue(job.clientName)}
                          </div>
                          <div className="text-[11px] text-gray-400 truncate">
                            {formatDisplayValue(job.clientAddress)}
                            {device ? ` · ${device}` : ''}
                          </div>
                        </div>
                        <span className="text-[11px] text-blue-300 whitespace-nowrap">{dateLabel}</span>
                      </div>
                    </button>
                  );
                })}
                {filteredJobs.length > 80 && (
                  <p className="text-[11px] text-gray-400 pt-1">
                    Prikazano prvih 80. Sužite pretragu za preciznije rezultate.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Calendar view */}
        <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  setCurrentDate((prev) => {
                    const d = new Date(prev);
                    if (viewMode === 'week') {
                      d.setDate(d.getDate() - 7);
                    } else {
                      d.setMonth(d.getMonth() - 1);
                    }
                    return d;
                  })
                }
                className="p-2 rounded-md bg-gray-700 text-gray-200 hover:bg-gray-600"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="text-sm sm:text-base text-white font-medium">
                {currentDate.toLocaleDateString('sr-RS', {
                  month: 'long',
                  year: 'numeric'
                })}
              </div>
              <button
                onClick={() =>
                  setCurrentDate((prev) => {
                    const d = new Date(prev);
                    if (viewMode === 'week') {
                      d.setDate(d.getDate() + 7);
                    } else {
                      d.setMonth(d.getMonth() + 1);
                    }
                    return d;
                  })
                }
                className="p-2 rounded-md bg-gray-700 text-gray-200 hover:bg-gray-600"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-1 rounded-md bg-gray-700 text-xs sm:text-sm text-gray-200 hover:bg-gray-600"
            >
              Danas
            </button>
          </div>

          {/* Day names header - hidden on mobile, shown on desktop */}
          <div className="hidden sm:grid grid-cols-7 gap-1 mb-1">
            {dayNames.map((name) => (
              <div
                key={name}
                className="text-center text-xs text-gray-400 uppercase tracking-wide"
              >
                {name}
              </div>
            ))}
          </div>

          {viewMode === 'week' ? (
            <WeekView
              currentDate={currentDate}
              jobsByDate={jobsByDate}
              onSelectJob={(job) => {
                setIsPrintModalOpen(false);
                setSelectedJob(job);
              }}
            />
          ) : (
            <MonthView
              currentDate={currentDate}
              jobsByDate={jobsByDate}
              onSelectJob={(job) => {
                setIsPrintModalOpen(false);
                setSelectedJob(job);
              }}
            />
          )}
        </div>

        {/* Job details modal */}
        {selectedJob && !isEditModalOpen && !isReportEditModalOpen && !createJobPrefill && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-60 p-4">
            <div className="bg-gray-900 rounded-lg p-4 sm:p-6 max-w-2xl w-full shadow-xl border border-gray-700 max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-700">
                <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
                  <Archive className="h-5 w-5 text-blue-400" />
                  Detalji intervencije
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsPrintModalOpen(true)}
                    className="flex items-center justify-center p-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                    title="Štampaj radni nalog"
                  >
                    <Printer className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleCreateJobForSameClient}
                    className="flex items-center justify-center p-1.5 bg-green-500 hover:bg-green-400 text-white rounded transition-colors"
                    title="Novi posao za ovog klijenta"
                  >
                    <PlusCircle className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleEditJob}
                    className="flex items-center justify-center p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    title="Izmeni posao"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  {selectedJob?.report && (
                    <button
                      onClick={handleEditReport}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm rounded transition-colors"
                      title="Izmeni naplatu i troškove"
                    >
                      <DollarSign className="h-4 w-4" />
                      <span className="hidden sm:inline">Naplata</span>
                    </button>
                  )}
                  <button
                    onClick={handleDeleteJob}
                    className="flex items-center justify-center p-1.5 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    title="Obriši posao"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedJob(null);
                      setIsPrintModalOpen(false);
                    }}
                    className="text-gray-400 hover:text-white p-1"
                    title="Zatvori"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto pr-2 space-y-4 text-sm">
                {/* Klijent i kontakt */}
                <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
                  <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wide">Uređaj i radnik</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-400 mb-2">Uređaj</div>
                      <div className="text-sm text-white">
                        {[(selectedJob.deviceType || '').trim(), (selectedJob.deviceBrand || '').trim(), (selectedJob.deviceModel || '').trim()].filter(Boolean).join(' ') || '-'}
                      </div>
                      {selectedJob.deviceSerialNumber && (
                        <div className="text-xs text-gray-400 mt-2">
                          Serijski broj: {selectedJob.deviceSerialNumber.replace(/N\/A|N\/a|n\/a/gi, '-')}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 mb-2">
                        {selectedJob.sourceCompanyId &&
                        String(selectedJob.sourceCompanyId) === String(currentCompanyId)
                          ? 'Servisni partner'
                          : 'Radnik'}
                      </div>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const displayAssignee =
                            resolveOutgoingHandoffPartnerAssignee(
                              selectedJob,
                              partners,
                              currentCompanyId
                            ) || selectedJob.assignedTo;
                          if (!displayAssignee) return <span className="text-sm text-white">-</span>;
                          return (
                            <>
                              <div
                                className="h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm flex-shrink-0"
                                style={{
                                  backgroundColor: getAssigneeColor(
                                    displayAssignee,
                                    workers,
                                    partners
                                  ),
                                  background: getAssigneeColor(
                                    displayAssignee,
                                    workers,
                                    partners
                                  )
                                }}
                                title={getWorkerName(displayAssignee, workers, partners)}
                              >
                                {getWorkerInitials(displayAssignee, workers, partners)}
                              </div>
                              <span className="text-sm text-white">
                                {getWorkerName(displayAssignee, workers, partners) || '-'}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Opis */}
                <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
                  <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wide">Opis</h3>
                  <div className="space-y-2">
                    {formatDisplayValue(selectedJob.issueDescription) !== '-' && (
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Kvar</div>
                        <div className="text-sm text-white whitespace-pre-line">
                          {formatDisplayValue(selectedJob.issueDescription)}
                        </div>
                      </div>
                    )}
                    {formatDisplayValue(selectedJob.report?.workDescription) !== '-' && (
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Šta je rađeno</div>
                        <div className="text-sm text-white whitespace-pre-line">
                          {formatDisplayValue(selectedJob.report?.workDescription)}
                        </div>
                      </div>
                    )}
                    {formatDisplayValue(selectedJob.issueDescription) === '-' && formatDisplayValue(selectedJob.report?.workDescription) === '-' && (
                      <div className="text-sm text-gray-400">-</div>
                    )}
                  </div>
                </div>

                {/* Vreme i trajanje */}
                <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
                  <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wide">Vreme i trajanje</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-400 mb-2">Završeno</div>
                      <div className="text-sm text-white">
                        {new Date(
                          selectedJob.report?.completedAt ||
                            selectedJob.report?.submittedAt ||
                            selectedJob.serviceDate ||
                            selectedJob.updatedAt ||
                            selectedJob.createdAt
                        ).toLocaleString('sr-RS')}
                      </div>
                      {selectedJob.firstVisitDate && (
                        <div className="text-xs text-gray-400 mt-2">
                          Prvi put: {new Date(selectedJob.firstVisitDate).toLocaleDateString('sr-RS')}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 mb-2">Trajanje</div>
                      <div className="space-y-1">
                        <div className="text-sm text-white">
                          Put: {(() => {
                            const t = selectedJob.report?.totalTravelTime || 0;
                            const h = Math.floor(t / 3600);
                            const m = Math.floor((t % 3600) / 60);
                            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                          })()}
                        </div>
                        <div className="text-sm text-white">
                          Rad: {(() => {
                            const t = selectedJob.report?.totalWorkTime || 0;
                            const h = Math.floor(t / 3600);
                            const m = Math.floor((t % 3600) / 60);
                            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Naplata i troškovi */}
                <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
                  <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wide">Naplata i troškovi</h3>
                  {(() => {
                    // Calculate costs
                    let reversiCost = selectedJob.report?.reversiCost || 0;
                    if (reversiCost === 0 && selectedJob.report?.usedSpareParts && Array.isArray(selectedJob.report.usedSpareParts) && selectedJob.report.usedSpareParts.length > 0) {
                      reversiCost = selectedJob.report.usedSpareParts.reduce((sum, part) => {
                        if (!part) return sum;
                        const price = typeof part.price === 'number' ? part.price : parseFloat(part.price) || 0;
                        const quantity = typeof part.quantity === 'number' ? part.quantity : parseFloat(part.quantity) || 1;
                        return sum + (price * quantity);
                      }, 0);
                    }
                    
                    let additionalMaterialsCost = selectedJob.report?.additionalMaterialsCost || 0;
                    if (additionalMaterialsCost === 0 && selectedJob.report?.additionalMaterials && Array.isArray(selectedJob.report.additionalMaterials) && selectedJob.report.additionalMaterials.length > 0) {
                      additionalMaterialsCost = selectedJob.report.additionalMaterials.reduce((sum, material) => {
                        if (!material) return sum;
                        const price = typeof material.price === 'number' ? material.price : parseFloat(material.price) || 0;
                        return sum + price;
                      }, 0);
                    }
                    
                    const amortizationCost = selectedJob.report?.amortizationCost || 0;
                    const totalCost = selectedJob.report?.totalCost || (reversiCost + additionalMaterialsCost + amortizationCost);
                    
                    // Calculate revenue
                    const revenue = parseMoneyAmountSR(selectedJob.report?.serviceCharged);
                    
                    // Calculate profit
                    const profit = revenue - totalCost;
                    
                    return (
                      <div className="space-y-2">
                        {/* Top row: Naplaćena usluga i Čista zarada */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-xs text-gray-400 mb-1">Naplaćena usluga</div>
                            <div className="text-base text-green-400 font-semibold">
                              {selectedJob.report?.serviceCharged || 'Nema unete naplate'}
                            </div>
                          </div>
                          {revenue > 0 && (
                            <div>
                              <div className="text-xs text-gray-400 mb-1">Čista zarada</div>
                              <div className={`text-base font-semibold ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {profit.toLocaleString('sr-RS')} RSD
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Trošak intervencije - kompaktno */}
                        {totalCost > 0 && (
                          <div className="pt-2 border-t border-gray-700">
                            <div className="flex items-center justify-between mb-1">
                              <div className="text-xs text-gray-400">Trošak intervencije</div>
                              <div className="text-sm text-red-400 font-semibold">
                                {totalCost.toLocaleString('sr-RS')} RSD
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-[10px] text-gray-400">
                              {reversiCost > 0 && (
                                <div>
                                  <div className="text-gray-500">Materijal:</div>
                                  <div className="text-gray-300">{reversiCost.toLocaleString('sr-RS')} RSD</div>
                                </div>
                              )}
                              {additionalMaterialsCost > 0 && (
                                <div>
                                  <div className="text-gray-500">Dodatni:</div>
                                  <div className="text-gray-300">{additionalMaterialsCost.toLocaleString('sr-RS')} RSD</div>
                                </div>
                              )}
                              {amortizationCost > 0 && (
                                <div>
                                  <div className="text-gray-500">Amortizacija:</div>
                                  <div className="text-gray-300">{amortizationCost.toLocaleString('sr-RS')} RSD</div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Materijali (opis) */}
                        {selectedJob.report?.materialsUsed && (
                          <div className="pt-2 border-t border-gray-700">
                            <div className="text-xs text-gray-400 mb-1">Materijali</div>
                            <div className="text-xs text-white">{selectedJob.report.materialsUsed}</div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Potpis stranke */}
                {selectedJob.report?.customerSignature && (
                  <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
                    <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wide">Potpis stranke</h3>
                    <div className="border border-gray-600 rounded-md p-2 bg-white inline-block">
                      <img
                        src={selectedJob.report.customerSignature}
                        alt="Potpis stranke"
                        className="max-h-24 w-auto object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {isPrintModalOpen && selectedJob && (
          <WorkOrderPrintModal
            job={selectedJob}
            onClose={() => setIsPrintModalOpen(false)}
          />
        )}
        
        {/* Create Job for same client */}
        {createJobPrefill && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
            <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <h2 className="text-lg font-semibold text-white">Novi posao za klijenta</h2>
                <button
                  onClick={() => setCreateJobPrefill(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                <JobForm
                  isEdit={false}
                  jobData={createJobPrefill}
                  onClose={() => setCreateJobPrefill(null)}
                  isModal={true}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Edit Job Modal */}
        {isEditModalOpen && selectedJob && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
            <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <h2 className="text-lg font-semibold text-white">Izmeni posao</h2>
                <button
                  onClick={handleCloseEditModal}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                <JobForm
                  isEdit={true}
                  jobData={selectedJob}
                  onClose={handleCloseEditModal}
                  isModal={true}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Edit Report Modal */}
        {isReportEditModalOpen && selectedJob && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
            <div className="bg-gray-900 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-400" />
                  Izmeni naplatu i troškove
                </h2>
                <button
                  onClick={handleCloseReportEditModal}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                <ReportEditForm
                  job={selectedJob}
                  onClose={handleCloseReportEditModal}
                  onSuccess={handleReportEditSuccess}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Weekly view component
const WeekView = ({ currentDate, jobsByDate, onSelectJob }) => {
  const start = startOfWeekHelper(currentDate);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2 sm:gap-2">
      {days.map((day) => {
        const key = formatLocalDateKey(day) || '';
        const jobs = jobsByDate[key] || [];
        return (
          <div
            key={key}
            className="min-h-[120px] sm:min-h-[110px] border border-gray-700 rounded-lg bg-gray-900/60 flex flex-col"
          >
            <div className="px-1.5 py-1 border-b border-gray-700 text-[10px] sm:text-xs text-gray-300 flex justify-between items-center">
              <span>
                {day.toLocaleDateString('sr-RS', {
                  day: '2-digit',
                  month: '2-digit'
                })}
              </span>
              <span className="text-[10px] text-gray-500">
                {jobs.length} pos.
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-1 space-y-1">
              {jobs.map((job) => (
                <button
                  key={job._id}
                  onClick={() => onSelectJob(job)}
                  className="w-full text-left bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/40 rounded-md px-1.5 py-1 text-[10px] sm:text-xs text-gray-100"
                >
                  <div className="truncate font-medium">
                    {formatDisplayValue(job.clientAddress || job.clientName)}
                  </div>
                  <div className="truncate text-[10px] text-gray-300">
                    {formatDisplayValue(job.issueDescription)}
                  </div>
                </button>
              ))}
              {jobs.length === 0 && (
                <div className="text-[10px] text-gray-500 text-center mt-2">
                  Nema poslova
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Monthly view component
const MonthView = ({ currentDate, jobsByDate, onSelectJob }) => {
  const start = startOfMonthGridHelper(currentDate);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }

  const currentMonth = currentDate.getMonth();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2 sm:gap-2">
      {days.map((day) => {
        const key = formatLocalDateKey(day) || '';
        const jobs = jobsByDate[key] || [];
        const isOtherMonth = day.getMonth() !== currentMonth;

        return (
          <div
            key={key}
            className={`min-h-[120px] sm:min-h-[100px] border rounded-lg flex flex-col ${
              isOtherMonth
                ? 'border-gray-800 bg-gray-900/40'
                : 'border-gray-700 bg-gray-900/60'
            }`}
          >
            <div className="px-1.5 py-1 border-b border-gray-800 text-[10px] sm:text-xs text-gray-300 flex justify-between items-center">
              <span>{day.getDate()}</span>
              {jobs.length > 0 && (
                <span className="text-[10px] text-blue-400">
                  {jobs.length}
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-1 space-y-1">
              {jobs.slice(0, 3).map((job) => (
                <button
                  key={job._id}
                  onClick={() => onSelectJob(job)}
                  className="w-full text-left bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/40 rounded-md px-1.5 py-0.5 text-[10px] sm:text-[11px] text-gray-100"
                >
                  <div className="truncate">
                    {formatDisplayValue(job.clientAddress || job.clientName)}
                  </div>
                </button>
              ))}
              {jobs.length > 3 && (
                <div className="text-[10px] text-gray-400">
                  +{jobs.length - 3} još
                </div>
              )}
              {jobs.length === 0 && (
                <div className="text-[10px] text-gray-700 text-center mt-2">
                  -
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Helpers for child components (avoiding re-definition of logic)
const startOfWeekHelper = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const startOfMonthGridHelper = (date) => {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const day = firstDay.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  firstDay.setDate(firstDay.getDate() + diff);
  firstDay.setHours(0, 0, 0, 0);
  return firstDay;
};

export default ArchiveView;
